import { useState, useEffect } from "react";
import { Phone, Menu, Lock } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_LINKS = [
  { label: "O nás", href: "#about" },
  { label: "Galerie", href: "#gallery" },
  { label: "Snídaně", href: "#breakfast-menu" },
  { label: "Stálé menu", href: "#evening-menu" },
  { label: "Polední menu", href: "#lunch-menu" },
  { label: "Sváteční menu", href: "#holiday-menu" },
  { label: "Kontakt", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header data-testid="main-header" className="fixed top-0 w-full z-50">
      {/* Phone bar */}
      <div className="bg-[#051F16] border-b border-[#1D4133]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-9">
          <span className="text-xs tracking-widest uppercase text-[#94A3B8]">
            Veranda pod Soutokem
          </span>
          <a
            href="tel:+420733513535"
            data-testid="phone-link-header"
            className="flex items-center gap-2 text-[#EAB308] text-sm font-semibold hover:text-yellow-300 transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="hidden sm:inline text-[#94A3B8] font-normal mr-1">Pro rezervaci volejte</span>
            +420 733 513 535
          </a>
        </div>
      </div>

      {/* Navigation */}
      <nav
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-[#02120D]/95 backdrop-blur-md shadow-xl shadow-black/30"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2"
            data-testid="logo-btn"
          >
            <span className="font-['Playfair_Display'] text-lg sm:text-xl font-semibold text-[#F0FDF4] tracking-tight">
              Veranda <span className="text-[#EAB308]">pod Soutokem</span>
            </span>
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="text-sm tracking-wider uppercase text-[#94A3B8] hover:text-[#EAB308] transition-colors duration-300"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </button>
            ))}
            <a
              href="/admin/login"
              className="text-[#94A3B8]/50 hover:text-[#EAB308] transition-colors duration-300"
              data-testid="nav-admin-link"
              title="Správa webu"
            >
              <Lock className="w-4 h-4" />
            </a>
          </div>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden text-[#F0FDF4] p-2"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#051F16] border-l border-[#1D4133] w-72"
            >
              <div className="flex flex-col gap-2 mt-8">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollTo(link.href)}
                    className="text-left px-4 py-3 text-lg tracking-wide text-[#E2E8F0] hover:text-[#EAB308] hover:bg-[#0B2A20] rounded transition-all"
                    data-testid={`mobile-nav-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </button>
                ))}
                <div className="mt-6 px-4 space-y-4">
                  <a
                    href="tel:+420733513535"
                    className="flex items-center gap-2 text-[#EAB308] font-semibold"
                  >
                    <Phone className="w-4 h-4" />
                    +420 733 513 535
                  </a>
                  <a
                    href="/admin/login"
                    className="flex items-center gap-2 text-[#94A3B8]/60 text-sm hover:text-[#EAB308] transition-colors"
                    data-testid="mobile-admin-link"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Správa webu
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
